import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Grid, GridItem, Text, Tooltip} from '@chakra-ui/react'
import i18next from 'i18next'
import {useTranslation} from 'react-i18next'
import {SolidWarningIcon} from '../../../icons'
import BillActionButton from '../BillActionButton'
import UpdatePayeeButton from '../UpdatePayeeButton'
import StatusBadge from './StatusBadge'
import LabelValue from './LabelValue'
import {hasMissingPayeeDetails} from '../payeeDetails'

const BillCard = ({bill, onSelectPayee}) => {
  i18next.loadNamespaces('pointsOnPayments')
  const {t} = useTranslation('pointsOnPayments')
  const {payeeName, payee, billNumber, lifecycleStatus, dueAmount, mcoin} = bill

  const detailsMissing = hasMissingPayeeDetails(payee)

  return (
    <Box borderTop="1px solid" borderColor="mld.neutral.200" pt="sm" pb="sm">
      <Box display="inline-block">
        <StatusBadge lifecycleStatus={lifecycleStatus} t={t} />
      </Box>

      <Grid templateColumns="1fr auto" gap="xs" mt="xs" alignItems="start">
        <GridItem>
          {detailsMissing ? (
            <Tooltip
              className="fs-unmask"
              label={t('bill.table.payeeMissingDetails.missing')}
              placement="top"
              hasArrow
            >
              <Flex
                className="fs-mask"
                alignItems="center"
                gap="xxs"
                cursor="default"
                width="fit-content"
              >
                <Text fontWeight="semibold" color="mld.warning.900">
                  {payeeName}
                </Text>
                <SolidWarningIcon boxSize="16px" color="mld.warning.700" />
              </Flex>
            </Tooltip>
          ) : (
            <Text fontWeight="semibold" className="fs-mask">
              {payeeName}
            </Text>
          )}
          <Text fontSize="sm" color="mld.neutral.700" mt="xxs">
            {t('bill.table.referenceLabel', {billNumber})}
          </Text>
        </GridItem>

        <GridItem>
          <LabelValue
            label={t('bill.table.card.balanceDue')}
            value={dueAmount.toFormat()}
          />
          {mcoin && (
            <>
              <LabelValue
                label={t('bill.table.card.mCoin')}
                value={
                  <Tooltip
                    label={t('bill.table.columns.body.mCoinTooltip')}
                    placement="bottom"
                    hasArrow
                  >
                    <Text as="span" cursor="default">
                      <Text as="span" color="mld.success.700">
                        +
                      </Text>{' '}
                      {mcoin.amount.toLocaleString()}
                    </Text>
                  </Tooltip>
                }
                valueDotted
              />
              {mcoin.fee && (
                <LabelValue
                  label={t('bill.table.card.fee')}
                  value={
                    <Tooltip
                      label={t('bill.table.columns.body.feeTooltip')}
                      placement="bottom"
                      hasArrow
                    >
                      <Text as="span" cursor="default">
                        {mcoin.fee.toFormat()}
                      </Text>
                    </Tooltip>
                  }
                  valueDotted
                />
              )}
            </>
          )}
        </GridItem>
      </Grid>

      <Box mt="sm" display="inline-block">
        {detailsMissing ? (
          <UpdatePayeeButton payee={payee} onSelectPayee={onSelectPayee} />
        ) : (
          <BillActionButton bill={bill} />
        )}
      </Box>
    </Box>
  )
}

BillCard.propTypes = {
  bill: PropTypes.shape({
    id: PropTypes.string.isRequired,
    payeeName: PropTypes.string,
    payee: PropTypes.shape({}),
    billNumber: PropTypes.string,
    lifecycleStatus: PropTypes.string,
    dueAmount: PropTypes.shape({toFormat: PropTypes.func}).isRequired,
    mcoin: PropTypes.shape({
      amount: PropTypes.number,
      fee: PropTypes.shape({toFormat: PropTypes.func}),
    }),
    canPay: PropTypes.bool,
  }).isRequired,
  onSelectPayee: PropTypes.func.isRequired,
}

export default BillCard
